// Utility for mapping split_blob_url.class values to valid document classifications
import { DOCUMENT_CLASSIFICATIONS, DocumentCategory } from "@/constants/documents";
import { toTitleCase } from "@/utils/string";

export type ValidClassification = DocumentCategory;

// Build a quick lookup mapping of normalized category keys -> display title.
// Keys are normalized by replacing underscores with spaces and uppercasing.
const CATEGORY_TITLE_MAP: Record<string, string> = DOCUMENT_CLASSIFICATIONS.reduce(
  (acc, doc) => {
    const key = doc.category.replace(/_/g, " ").toUpperCase();
    acc[key] = doc.title;
    // Also add the raw category uppercased (covers already spaced values)
    acc[doc.category.toUpperCase()] = doc.title;
    return acc;
  },
  {} as Record<string, string>
);

/**
 * Maps a classification string from split_blob_url.class to a valid classification
 * Handles case insensitivity and trailing numerical digits
 *
 * @param inputClass - The classification string from split_blob_url.class
 * @returns A valid document classification
 */
export const mapToValidClassification = (
  inputClass: string
): ValidClassification => {
  if (!inputClass) {
    return DocumentCategory.MEDICAL_RECORDS_AND_REPORTS; // Default if no classification is provided
  }

  // Remove any trailing numerical digits and trim whitespace
  const cleanedInput = inputClass.replace(/\s+\d+$/, "").trim();

  // Try to find an exact match (case-insensitive)
  const exactMatch = Object.values(DocumentCategory).find(
    (validClass) => validClass.toUpperCase() === cleanedInput.toUpperCase()
  );
  if (exactMatch) {
    return exactMatch as DocumentCategory;
  }

  // If no exact match, try to find a partial match
  for (const validClass of Object.values(DocumentCategory)) {
    if (validClass.length <= 3) continue;
    const normalizedValid = validClass.toUpperCase();
    const normalizedInput = cleanedInput.toUpperCase();
    if (
      normalizedValid.includes(normalizedInput) ||
      normalizedInput.includes(normalizedValid)
    ) {
      return validClass as DocumentCategory;
    }
  }

  // Default to MEDICAL_RECORDS_AND_REPORTS if no match is found
  return DocumentCategory.MEDICAL_RECORDS_AND_REPORTS;
};

/**
 * Gets the proper title for a document category from DOCUMENT_CLASSIFICATIONS constants.
 * If the category doesn't exist in the constants, applies title casing to the provided string.
 *
 * @param category - The category code or name
 * @returns The proper title for the category
 *
 * @example
 * formatDocumentCategory("LOI") // Returns "Letter of Instruction"
 * formatDocumentCategory("MEDICAL HISTORY") // Returns "Medical History"
 */
export function formatDocumentCategory(category: string | undefined | null): string {
  if (!category) return "";
  const key = category.replace(/_/g, " ").trim().toUpperCase();
  return CATEGORY_TITLE_MAP[key] || toTitleCase(category.trim());
}
